import { useEffect } from 'react';
import { getTimezoneOffsetString } from '../utils/dateHelpers';
import useNotification from './useNotification';
import { useRegeneratePost, useSendFeedback, useUpdatePost } from '../mutations/post';
import { useApprovePost, useCancelPost } from '../mutations/social';
import { usePostModalStore } from '../store/usePostModalStore';
import { useQueryClient } from '@tanstack/react-query';
import { useGetPostById } from '../queries/post';
import useErrorModalStore from '../store/useErrorModalStore';
import { getErrorModalMessage } from '../utils/errorMessages';
import { getRedirectUrl } from '../utils/helpers';
import { useGetSocialAccounts } from '../queries/social';
import { useAppStore } from '../store/useAppStore';
/**
 * Custom hook to manage single post logic:
 * states, queries, mutations, handlers, and effects
 * Reads post from postModalStore
 * @returns {Object} - Returns handlers and data needed by PostModal
 */
export const useSinglePost = () => {
  // States
  const queryClient = useQueryClient();
  const { isOpen, post, closeModal, setPost, setInfo } = usePostModalStore();
  const { socialAccounts: storedSocialAccounts } = useAppStore();
  const notification = useNotification();
  const { openErrorModal } = useErrorModalStore();
  // Only fetch social accounts if modal is open and we don't have cached data
  const { data: socialAccounts = [] } = useGetSocialAccounts(
    isOpen && storedSocialAccounts === null
  );
  const redirectToUpgrade = getRedirectUrl('/dashboard/products');

  // Queries
  const { data: postData, isLoading: isPostLoading } = useGetPostById(post?.id, true);

  // Sync store post with latest data outside of render phase
  useEffect(() => {
    if (!postData || !post) return;
    if (postData.id === post.id) {
      setPost(postData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postData, post?.id]);

  // Mutations
  const { mutate: regeneratePost } = useRegeneratePost(
    () => {
      notification.success('Post regenerated successfully!');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      closeModal();
    },
    (error) => {
      if (error?.status === 429) {
        setInfo({
          message: 'Regeneration quota exceeded',
          actionText: 'Upgrade Now',
          actionLink: redirectToUpgrade,
        });

        return;
      }

      openErrorModal(
        getErrorModalMessage('post_regeneration_error'),
        'post_regeneration_error',
        null,
        { postId: post?.id }
      );
    }
  );
  const { mutate: updatePost } = useUpdatePost(
    () => {
      notification.success('Post updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['post-details', post?.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    (error) => {
      console.error('Failed to update post:', error);
      openErrorModal(getErrorModalMessage('post_update_error'), 'post_update_error', null, {
        postId: post?.id,
      });
    }
  );
  const { mutate: approvePost } = useApprovePost(
    () => {
      notification.success('Post scheduled successfully!');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      closeModal();
    },
    (error) => {
      console.error('Failed to schedule post:', error);
      openErrorModal(getErrorModalMessage('post_schedule_error'), 'post_schedule_error', null, {
        postId: post?.id,
      });
    }
  );

  const { mutate: cancelPost } = useCancelPost(
    () => {
      notification.success('Post cancelled and reverted to draft.');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      closeModal();
    },
    (error) => {
      console.error('Failed to cancel post:', error);
      openErrorModal(getErrorModalMessage('general_error'), 'general_error', null, {
        postId: post?.id,
      });
    }
  );

  const { mutate: sendFeedback } = useSendFeedback(
    () => {
      notification.success('Feedback sent successfully!');
    },
    (error) => {
      console.error('Failed to send feedback:', error);
      openErrorModal(getErrorModalMessage('post_feedback_error'), 'post_feedback_error', null, {
        postId: post?.id,
      });
    }
  );

  // HandleFunctions
  const handleEditSubmit = ({ postId, data, error }) => {
    if (error) {
      openErrorModal(getErrorModalMessage('post_edit_error'), 'general_error');
      return;
    }
    if (!postId) return;
    updatePost({ postId, data });
  };

  const handleScheduleSubmit = (selectedSocialAccounts) => {
    if (!post?.id) return;
    approvePost({
      postId: post.id,
      platforms: selectedSocialAccounts,
      timezone: getTimezoneOffsetString(),
    });
  };

  const handleCancelPost = (platforms) => {
    if (!post?.id) return;
    cancelPost({ postId: post.id, platforms, timezone: getTimezoneOffsetString() });
  };
  const handleRegenerateSubmit = (userInput) => {
    if (!post?.id || !userInput.trim()) return;
    regeneratePost({
      postId: post.id,
      userInput: userInput.trim(),
    });
  };

  const handleFeedbackSubmit = (userInput) => {
    if (!post?.id || !userInput.trim()) return;
    // For feedback, we use the updatePost mutation to send feedback as part of post data
    sendFeedback({
      message: userInput.trim(),
      post_id: post.id,
    });
  };

  // Return all necessary data and handlers
  return {
    postData,
    socialAccounts,
    isPostLoading,
    handleEditSubmit,
    handleScheduleSubmit,
    handleCancelPost,
    handleRegenerateSubmit,
    handleFeedbackSubmit,
    isOpen,
    post,
    closeModal,
    setPost,
  };
};
